import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { useLanguage } from "../../../contexts/LanguageContext";
import { DeliveryStatusPill, ErrorBox, outlineButtonClass } from "./ui";

const formatDate = (value, language) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(language || undefined, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
};

// Recent deliveries of a webhook (the API returns the latest 50). Fits the
// drawer: four columns, and the bordered box scrolls sideways on its own if
// a narrow screen still can't fit them.
// A failed poll shows its error above the last rows that did load.
export function DeliveriesTable({ deliveries, isLoading, errorLines, onView }) {
  const { t, language } = useLanguage();

  return (
    <section aria-labelledby="deliveries-title" className="flex flex-col gap-2">
      <span className="flex flex-col gap-0.5">
        <h3 id="deliveries-title" className="text-sm font-semibold text-gray-900">
          {t("integrations.webhook.recentEvents")}
        </h3>
        <span className="text-xs leading-normal text-gray-600">
          {t("integrations.deliveries.retryNote")}
        </span>
      </span>
      <ErrorBox lines={errorLines} />
      {!deliveries.length ? (
        errorLines?.length ? null : (
        <p className="rounded-xl border border-dashed border-gray-300 px-4 py-5 text-center text-sm text-gray-600">
          {isLoading ? t("common.loading") : t("integrations.deliveries.empty")}
        </p>
        )
      ) : (
        <div className="relative overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full min-w-[480px] border-collapse text-[13px]">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th scope="col" className="w-[38%] px-2.5 py-2 text-xs font-semibold text-gray-600">
                  {t("integrations.webhook.eventName")}
                </th>
                <th scope="col" className="px-2.5 py-2 text-xs font-semibold text-gray-600">
                  {t("integrations.webhook.statusLabel")}
                </th>
                <th scope="col" className="px-2.5 py-2 text-right text-xs font-semibold text-gray-600">
                  {t("integrations.webhook.responseCode")}
                </th>
                <th scope="col" className="px-2.5 py-2 text-xs font-semibold text-gray-600">
                  {t("integrations.webhook.date")}
                </th>
                <th scope="col" className="w-16 px-2.5 py-2">
                  <span className="sr-only">{t("integrations.deliveries.details")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((delivery) => {
                const date = formatDate(delivery.created_at, language);
                return (
                  <tr key={delivery.id} className="border-t border-gray-100">
                    <td className="px-2.5 py-2 font-mono text-xs break-all text-gray-900">
                      {delivery.event}
                    </td>
                    <td className="px-2.5 py-2">
                      <span className="flex flex-col items-start gap-0.5">
                        <DeliveryStatusPill status={delivery.status} />
                        <span className="text-[11px] text-gray-600 tabular-nums">
                          {t("integrations.deliveries.attemptCount", {
                            count: delivery.attempts ?? 0,
                          })}
                        </span>
                      </span>
                    </td>
                    <td className="px-2.5 py-2 text-right text-gray-700 tabular-nums">
                      {delivery.response_code || "—"}
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap text-gray-700 tabular-nums">
                      {date}
                    </td>
                    <td className="px-1.5 py-1 text-right">
                      <button
                        type="button"
                        onClick={() => onView(delivery)}
                        aria-label={t("integrations.deliveries.viewLabel", {
                          event: delivery.event,
                          date,
                        })}
                        className={`${outlineButtonClass} min-h-8 px-2.5 text-xs`}
                      >
                        {t("integrations.deliveries.view")}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const pretty = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 2);
};

// One delivery, shown in place of the form (decision D4: a second panel in
// the drawer, not a modal over it). Headers are rendered as the API sends
// them; the API masks the Authorization token.
export function DeliveryDetail({ delivery, onBack }) {
  const { t, language } = useLanguage();
  const created = formatDate(delivery.created_at, language);
  const completed = formatDate(delivery.completed_at, language);

  const blocks = [
    { title: t("integrations.deliveries.payload"), body: pretty(delivery.payload) },
    { title: t("integrations.deliveries.headers"), body: pretty(delivery.request_headers) },
    {
      title: delivery.response_code
        ? t("integrations.deliveries.responseWithCode", { code: delivery.response_code })
        : t("integrations.deliveries.response"),
      body: pretty(delivery.response_body),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex min-h-9 items-center gap-1.5 self-start rounded-lg pr-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
      >
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
        {t("integrations.deliveries.back")}
      </button>
      <h3 className="flex flex-wrap items-center gap-2.5 text-[17px] font-semibold text-gray-900">
        <span className="font-mono break-all">{delivery.event}</span>
        <DeliveryStatusPill status={delivery.status} />
      </h3>
      {/* Explicit line height and margins: the rows overlapped in the drawer
          (e2e 2026-10-24) without them. */}
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-[13px] leading-5 text-gray-700 tabular-nums">
        <dt className="m-0 text-gray-600">{t("integrations.webhook.attempts")}</dt>
        <dd className="m-0">{delivery.attempts ?? 0}</dd>
        <dt className="m-0 text-gray-600">{t("integrations.deliveries.created")}</dt>
        <dd className="m-0">{created || "—"}</dd>
        <dt className="m-0 text-gray-600">{t("integrations.deliveries.completed")}</dt>
        <dd className="m-0">{completed || t("integrations.deliveries.notCompleted")}</dd>
      </dl>
      {blocks.map((block) => (
        <div key={block.title} className="flex flex-col gap-1.5">
          <h4 className="text-[13px] font-semibold text-gray-700">{block.title}</h4>
          <pre className="max-h-60 overflow-auto rounded-[10px] border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs leading-normal break-all whitespace-pre-wrap text-gray-900">
            {block.body}
          </pre>
        </div>
      ))}
    </div>
  );
}
